/**
 * How an application gets paid for what it sells — the Payments screen's API.
 *
 * This is the SECOND payment rail. `/api/billing` and `/api/payments` are
 * Twasta being paid; everything here is the PROJECT OWNER being paid, on
 * their own merchant account. The two never share credentials or orders.
 *
 * Administration only, on the platform host: a bound project domain serves
 * the running application's checkout (`/api/app-payments`), never the screen
 * that configures it.
 */

import api from './client'

export type GatewayProvider = 'razorpay' | 'stripe'
export type GatewayMode = 'test' | 'live'
/** own_keys — the project's own merchant account, Twasta takes nothing.
 *  brokered — the platform collects and settles to the project's linked
 *  account, keeping the instance's configured fee. */
export type AccountModel = 'own_keys' | 'brokered'

export interface PaymentGateway {
  id: number
  provider: GatewayProvider
  mode: GatewayMode
  account_model: AccountModel
  enabled: boolean
  key_id: string
  /** `••••3f9a` — enough to tell two keys apart, never enough to use one. */
  key_secret_masked: string
  webhook_secret_masked: string
  has_key_secret: boolean
  has_webhook_secret: boolean
  linked_account_id: string
  /** Paste this into the gateway's dashboard. */
  webhook_url: string
  currency: string
  capture_mode: 'auto' | 'manual'
  methods_allowed: string[]
  /** Echo of the project-level setting (see GatewayListResponse.cod_enabled). */
  cod_enabled: boolean
  brand_name: string
  brand_logo_url: string
  brand_color: string
  /** When a real call to the gateway last succeeded. Null after any
   *  credential change — a row must not keep claiming a key it no longer has. */
  verified_at: string | null
  last_error: string
  updated_at: string | null
}

export interface GatewayListResponse {
  /** `application.collect_payments` — the project switch that says this
   *  application takes its customers' money. Only the builder's list returns
   *  it; it decides whether the running app offers a Payments admin tab. */
  collect_payments?: boolean
  /** Whether the application accepts cash on delivery. A PROJECT setting —
   *  it needs no gateway, and it is on until a manager switches it off. */
  cod_enabled?: boolean
  gateways: PaymentGateway[]
  brokering_enabled: boolean
  platform_fee_percent: number
  providers: { name: string; enabled: boolean }[]
  known_methods: string[]
}

/** Partial by design: omit a secret to leave it alone, send "-" to clear it.
 *  The screen never receives a usable secret, so it cannot send one back. */
export interface GatewayUpdate {
  provider: GatewayProvider
  mode?: GatewayMode
  account_model?: AccountModel
  enabled?: boolean
  key_id?: string
  key_secret?: string
  webhook_secret?: string
  linked_account_id?: string
  currency?: string
  capture_mode?: 'auto' | 'manual'
  methods_allowed?: string[]
  cod_enabled?: boolean
  brand_name?: string
  brand_logo_url?: string
  brand_color?: string
}

export interface PaymentOrder {
  id: string
  status: 'created' | 'paid' | 'failed' | 'cancelled' | 'refunded' | 'partially_refunded'
  obj_name: string
  txn_id: string
  reference: string
  amount: number
  currency: string
  amount_refunded: number
  provider: string
  provider_order_id: string
  account_model: AccountModel
  platform_fee: number
  payer: { name: string; email: string; contact: string }
  created_at: string | null
  paid_at: string | null
  /** False on a paid order whose record could not be stamped — the money is
   *  real and the application does not know it yet. Retryable. */
  written_back: boolean
  writeback_error: string
}

export interface LedgerSummary {
  by_currency: {
    currency: string
    orders: number
    paid: number
    failed: number
    captured: number
    refunded: number
  }[]
  awaiting_writeback: number
}

export interface OrdersResponse {
  total: number
  orders: PaymentOrder[]
  summary: LedgerSummary
}

export interface WebhookEvent {
  id: number
  provider: string
  event_type: string
  status: 'received' | 'processed' | 'ignored' | 'failed'
  error: string
  created_at: string | null
}

export async function listGateways(project: string): Promise<GatewayListResponse> {
  const { data } = await api.get(`/payment-gateways/project/${encodeURIComponent(project)}`)
  return data
}

/** Flip the project's "collect payments" switch (DEPLOY). */
export async function setCollectPayments(
  project: string, enabled: boolean,
): Promise<boolean> {
  const { data } = await api.put(
    `/payment-gateways/project/${encodeURIComponent(project)}/collect`, { enabled })
  return !!data.collect_payments
}

/** Accept cash on delivery, or stop (DEPLOY). Needs no gateway. */
export async function setCashOnDelivery(
  project: string, enabled: boolean,
): Promise<boolean> {
  const { data } = await api.put(
    `/payment-gateways/project/${encodeURIComponent(project)}/cod`, { enabled })
  return !!data.cod_enabled
}

export async function saveGateway(
  project: string, body: GatewayUpdate,
): Promise<PaymentGateway> {
  const { data } = await api.put(
    `/payment-gateways/project/${encodeURIComponent(project)}`, body)
  return data.gateway
}

export async function testGateway(
  project: string, provider: GatewayProvider,
): Promise<{ ok: boolean; error: string; verified_at: string | null }> {
  const { data } = await api.post(
    `/payment-gateways/project/${encodeURIComponent(project)}/${provider}/test`)
  return data
}

export async function deleteGateway(
  project: string, provider: GatewayProvider,
): Promise<void> {
  await api.delete(
    `/payment-gateways/project/${encodeURIComponent(project)}/${provider}`)
}

export async function listOrders(
  project: string, params: { status?: string; limit?: number; offset?: number } = {},
): Promise<OrdersResponse> {
  const { data } = await api.get(
    `/payment-gateways/project/${encodeURIComponent(project)}/orders`, { params })
  return data
}

export async function refundOrder(
  project: string, orderId: string, amount?: number, reason = '',
): Promise<PaymentOrder> {
  const { data } = await api.post(
    `/payment-gateways/project/${encodeURIComponent(project)}/orders/${orderId}/refund`,
    { amount: amount ?? null, reason })
  return data.order
}

export async function retryWriteBack(
  project: string, orderId: string,
): Promise<PaymentOrder> {
  const { data } = await api.post(
    `/payment-gateways/project/${encodeURIComponent(project)}/orders/${orderId}/retry-writeback`)
  return data.order
}

export async function listWebhookEvents(
  project: string, limit = 50,
): Promise<WebhookEvent[]> {
  const { data } = await api.get(
    `/payment-gateways/project/${encodeURIComponent(project)}/webhook-events`,
    { params: { limit } })
  return data.events
}
